import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer from 'src/components/StyledComponents/StyledGridContainer';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { VoucherType } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useGetVoucherType } from 'src/services/voucherType.service';
import VoucherTypeActionMenuButton from 'src/components/ActionButtons/VoucherTypeActionMenu';

const VoucherTypeGrid: FC<{ state: boolean; searchTerm: string }> = ({
  state,
  searchTerm,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const [sortBy, setSortBy] = useState<string>('displayOrder');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'displayOrder', sort: 'asc' },
  ]);
  const {
    loading,
    voucherTypes,
    fetchVoucherTypes,
    deleteVoucherType,
    totalItems,
  } = useGetVoucherType();

  useEffect(() => {
    fetchVoucherTypes(page, pageSize, searchTerm, sortBy, order);
  }, [state, page, pageSize, searchTerm, sortBy, order]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const order = (newSortModel[0]?.sort || 'asc').toUpperCase();
    setPage(1);
    setSortBy(newSortModel[0]?.field || 'displayOrder');
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    { field: 'name', headerName: 'Name', flex: 0.75 },
    {
      field: 'displayOrder',
      headerName: 'Display Order',
      flex: 0.5,
      headerAlign: 'center',
      align: 'right',
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 3,
      sortable: false,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <VoucherTypeActionMenuButton
          rowId={params.id}
          name={params.row.name}
          status={params.row.status}
          fetchVoucherTypes={() =>
            fetchVoucherTypes(page, pageSize, searchTerm, sortBy, order)
          }
          deleteVoucherType={deleteVoucherType}
        />
      ),
    },
  ];

  const transformData = (voucherTypes: VoucherType[]) => {
    try {
      return voucherTypes.map((voucherType, index) => ({
        id: voucherType.id,
        number: index + 1,
        name: voucherType.name,
        displayOrder: voucherType.displayOrder,
        description: voucherType.description,
      }));
    } catch (error) {
      showSnackbar('Error transforming voucher type data', 'error');
      return [];
    }
  };
  const rows = transformData(voucherTypes);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        sortModel={sortModel}
        rows={rows}
        columns={columns}
        sortingMode="server"
        disableColumnMenu
        onSortModelChange={handleSortModelChange}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        sortingOrder={['asc', 'desc']}
      />
    </StyledGridContainer>
  );
};

export default VoucherTypeGrid;
